import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, Pencil, RotateCcw, Save, ShieldCheck, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ContractDocument } from '../components/ContractDocument';
import { HelpHint } from '../components/HelpHint';
import { PageNav } from '../components/PageNav';
import { useToast } from '../components/Toast';

/**
 * What each role is told it is answerable for.
 *
 * ## Why this screen exists at all
 *
 * The text ships as a draft written from what each role can actually do in the
 * system. That is a reasonable starting point and a poor final answer: the
 * person who knows what a dispatcher in Kisii is accountable for is the person
 * running the place, not whoever wrote the draft. A document nobody can change
 * without a developer goes stale, and then gets ignored, and then the
 * acceptance record attached to it means nothing.
 *
 * ## The switch that makes this workable
 *
 * Saving asks whether the change is substantive. It is the one thing the
 * software genuinely cannot decide for itself — a rewritten duty and a
 * corrected apostrophe are the same diff to a computer — and guessing either
 * way is bad: guess "yes" and a typo fix interrupts eight people's next
 * sign-in, guess "no" and a real change of duties passes unaccepted.
 *
 * So it is asked, the harmless answer is the default, and the screen says
 * exactly how many people the noisy answer will interrupt before it is chosen.
 */
export function ResponsibilitiesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [openRole, setOpenRole] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [substantive, setSubstantive] = useState(false);
  const [preview, setPreview] = useState(false);

  const all = useQuery({ queryKey: ['responsibilities'], queryFn: api.allResponsibilities });

  const rows = all.data?.responsibilities ?? [];
  const chosen = rows.find((row) => row.role === openRole);

  /*
   * The draft follows whichever role was opened, keyed by the role rather than
   * by the object — so a background refetch of the same role does not throw
   * away edits somebody is part-way through typing.
   */
  useEffect(() => {
    if (chosen) {
      setTitle(chosen.title);
      setBody(chosen.body);
      setSubstantive(false);
      setPreview(false);
    }
  }, [openRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => api.saveResponsibilities(openRole!, { title, body, substantive }),
    onSuccess: async (result) => {
      toast.done(
        result.reasked
          ? `Saved. Everybody with the ${result.role} role will be asked to accept it again.`
          : result.changed
            ? 'Saved. Nobody is asked to accept it again.'
            : 'Nothing had changed, so nothing was saved.',
      );
      await queryClient.invalidateQueries({ queryKey: ['responsibilities'] });
    },
    onError: (e: Error) => toast.failed(e.message),
  });

  const reset = useMutation({
    mutationFn: (role: string) => api.resetResponsibilities(role),
    onSuccess: async (result) => {
      toast.done(
        `${result.role} is back to the wording this system shipped with. Everybody in that role will be asked again.`,
      );
      await queryClient.invalidateQueries({ queryKey: ['responsibilities'] });
      setOpenRole(null);
    },
    onError: (e: Error) => toast.failed(e.message),
  });

  if (all.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  const dirty = Boolean(chosen) && (title !== chosen?.title || body !== chosen?.body);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageNav crumbs={[{ label: 'System', to: '/settings' }, { label: 'Responsibilities' }]} />

      <div>
        <h1 className="flex items-center gap-1.5 font-heading text-2xl">
          Role responsibilities
          <HelpHint>
            Each role is shown its own version once, after the terms of use, and cannot use the
            system until they accept it. Editing here changes what the next person sees; whether
            people who already accepted are asked again is your choice when you save.
          </HelpHint>
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          What each role is told it is answerable for. The wording below is a starting draft —
          change it to match how this business actually works.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <nav className="space-y-2">
          {rows.map((row) => {
            const allAccepted = row.holders > 0 && row.accepted >= row.holders;
            return (
              <button
                key={row.role}
                type="button"
                onClick={() => setOpenRole(row.role)}
                className={`w-full rounded-xl border p-3 text-left ${
                  openRole === row.role
                    ? 'border-primary-500 bg-primary-50/50'
                    : 'border-border hover:bg-muted/40'
                }`}
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium">{row.title}</span>
                  <span className="text-xs text-muted-foreground">v{row.version}</span>
                </span>
                <span className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Users className="h-3.5 w-3.5" aria-hidden="true" />
                  {row.holders === 0 ? (
                    'nobody holds this role'
                  ) : (
                    <>
                      {row.accepted} of {row.holders} accepted
                      {allAccepted && (
                        <ShieldCheck
                          className="h-3.5 w-3.5 text-secondary-600"
                          aria-label="everybody has accepted"
                        />
                      )}
                    </>
                  )}
                </span>
                {row.updatedById === null && (
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    still the wording this system shipped with
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <section className="card">
          {!chosen ? (
            <p className="text-sm text-muted-foreground">
              Choose a role on the left to read or change what it is answerable for.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <label className="min-w-[14rem] flex-1 text-sm">
                  Heading
                  <input
                    className="input mt-1 w-full"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Used in the panel title and in the sentence beside the checkbox.
                  </span>
                </label>

                <button
                  type="button"
                  className="btn-quiet"
                  onClick={() => setPreview((value) => !value)}
                >
                  {preview ? (
                    <>
                      <Pencil className="h-4 w-4" aria-hidden="true" /> Edit
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" aria-hidden="true" /> Preview
                    </>
                  )}
                </button>
              </div>

              {preview ? (
                <div className="max-h-[32rem] overflow-y-auto rounded-xl bg-muted/50 p-4">
                  {/* Exactly as the person accepting it will see it. */}
                  <ContractDocument body={body} className="text-sm" />
                </div>
              ) : (
                <label className="block text-sm">
                  What this role is answerable for
                  <textarea
                    className="input mt-1 h-[28rem] w-full font-mono text-xs leading-relaxed"
                    value={body}
                    spellCheck
                    onChange={(event) => setBody(event.target.value)}
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    <code>## </code> starts a heading and <code>- </code> a bullet. Anything you
                    write here is shown to that role and to nobody else. Leave it empty and nobody
                    in the role is asked to accept anything.
                  </span>
                </label>
              )}

              {/*
                The one decision the software cannot make.
                Stated as a consequence rather than as a setting: "ask N people
                again" is answerable, "bump version" is not.
              */}
              {dirty && (
                <label className="-m-2 flex cursor-pointer items-start gap-3 rounded-xl p-2 text-sm hover:bg-muted/40">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-5 w-5 shrink-0"
                    checked={substantive}
                    onChange={(event) => setSubstantive(event.target.checked)}
                  />
                  <span>
                    This changes what the role is actually answerable for
                    {chosen.holders > 0 && (
                      <>
                        {' '}
                        — ask{' '}
                        <strong>
                          {chosen.holders} {chosen.holders === 1 ? 'person' : 'people'}
                        </strong>{' '}
                        to read and accept it again
                      </>
                    )}
                    .
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      Leave this unticked for a correction — a typo, clearer wording, a
                      reordered bullet. Nobody is interrupted and the record stands.
                    </span>
                  </span>
                </label>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!dirty || save.isPending}
                  onClick={() => save.mutate()}
                >
                  {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  <Save className="h-4 w-4" aria-hidden="true" />
                  Save
                </button>

                {dirty && (
                  <button
                    type="button"
                    className="btn-quiet"
                    onClick={() => {
                      setTitle(chosen.title);
                      setBody(chosen.body);
                      setSubstantive(false);
                    }}
                  >
                    Undo my changes
                  </button>
                )}

                <button
                  type="button"
                  className="btn-quiet ml-auto"
                  disabled={reset.isPending}
                  onClick={() => reset.mutate(chosen.role)}
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Back to the original wording
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
